import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { baseURL } from '../api/config';

interface GapInfo {
  startTime: string;
  endTime: string;
  missingBars: number;
  gapNumber: number;
  duration: string;
  willBeFilled?: boolean;
}

interface GapDetectionResponse {
  symbol: string;
  timeframe: string;
  dateRange: {
    start: string;
    end: string;
  };
  totalBars: number;
  expectedBars: number;
  missingBars: number;
  gaps: GapInfo[];
  dataCompleteness: number;
}

interface GapFillPreview {
  previewId: string;
  metadata: {
    symbol: string;
    timeframe: string;
    sourcePlatform: string;
    totalBars: number;
    dateRange: {
      start: string;
      end: string;
    };
  };
  validation: {
    validBars: number;
    invalidBars: number;
    warnings: string[];
    errors: string[];
    readyToImport: boolean;
  };
  totalBars: number;
  readyToImport: boolean;
  gapsFilled: number;
}

interface ImportJob {
  jobId: string;
  status: string;
  barsInserted: number;
  barsSkipped: number;
  barsUpdated: number;
  gapsFilled: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

const Gaps = () => {
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [symbol, setSymbol] = useState('');
  const [timeframe, setTimeframe] = useState('M1');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [detecting, setDetecting] = useState(false);
  const [gapData, setGapData] = useState<GapDetectionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Gap fill workflow state
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<GapFillPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    loadAvailableSymbols();
    loadImportJobs();
  }, []);

  const loadAvailableSymbols = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/api/gaps/symbols`, {
        params: { timeframe: 'M1' },
        headers: { Authorization: `Bearer ${token}` }
      });

      // Map symbols: Remove '_c' suffix for display
      const symbols = (res.data || []).map((s: string) => s.replace(/_c$/, ''));
      setAvailableSymbols(symbols);

      // Set first symbol as default
      if (symbols.length > 0) {
        setSymbol(symbols[0]);
      }

      setLoadingSymbols(false);
    } catch (err) {
      console.error('Failed to load available symbols:', err);
      // Fallback to hardcoded symbols
      setAvailableSymbols(['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY']);
      setSymbol('XAUUSD');
      setLoadingSymbols(false);
    }
  };

  const loadImportJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/api/gaps/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportJobs(res.data || []);
    } catch (err) {
      console.error('Failed to load import jobs:', err);
    }
  };

  const detectGaps = async () => {
    if (!symbol.trim()) {
      setErrorMessage('Please enter a symbol');
      return;
    }

    setDetecting(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');

      // Convert date range dropdown to actual dates
      const now = new Date();
      let startDate: string | undefined = undefined;
      let endDate: string | undefined = undefined;

      if (dateRange !== 'All data') {
        endDate = now.toISOString();

        const daysMap: Record<string, number> = {
          'Last 7 days': 7,
          'Last 30 days': 30,
          'Last 90 days': 90
        };

        const days = daysMap[dateRange] || 30;
        const start = new Date();
        start.setDate(start.getDate() - days);
        startDate = start.toISOString();
      }

      // Map symbol: Add '_c' suffix for cTrader symbols (database uses XAUUSD_c format)
      const dbSymbol = symbol.includes('_c') ? symbol : `${symbol}_c`;

      const res = await axios.get(`${baseURL}/api/gaps/detect`, {
        params: {
          symbol: dbSymbol,
          timeframe,
          ...(startDate && { startDate }),
          ...(endDate && { endDate })
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setGapData(res.data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to detect gaps';
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      console.error('Gap detection failed:', err);
    } finally {
      setDetecting(false);
    }
  };

  const handleJsonFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.json')) {
        setJsonFile(selectedFile);
        setErrorMessage('');
      } else {
        setErrorMessage('Only JSON files from Python converter are supported');
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.json')) {
        setJsonFile(droppedFile);
        setErrorMessage('');
      } else {
        setErrorMessage('Only JSON files from Python converter are supported');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const uploadPreview = async () => {
    if (!jsonFile) {
      setErrorMessage('Please select a JSON file');
      return;
    }

    setUploading(true);
    setErrorMessage('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const jsonContent = e.target?.result as string;

        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${baseURL}/api/gaps/preview`,
          { jsonContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPreview(res.data);
        setUploading(false);
      };
      reader.readAsText(jsonFile);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload preview';
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setUploading(false);
      console.error('Preview upload failed:', err);
    }
  };

  const executeImport = async () => {
    if (!preview) return;

    setImporting(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${baseURL}/api/gaps/import`,
        { previewId: preview.previewId, overwriteExisting: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setImportJobs([res.data, ...importJobs]);
      setPreview(null);
      setJsonFile(null);
      setImporting(false);

      // Reload gaps after import
      setTimeout(() => detectGaps(), 2000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to execute import';
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      setImporting(false);
      console.error('Import failed:', err);
    }
  };

  const formatDuration = (duration: string) => {
    const match = duration.match(/(\d+):(\d+):(\d+)/);
    if (!match) return duration;
    const [_, hours, minutes] = match;
    if (parseInt(hours) > 0) {
      return `${parseInt(hours)}h ${parseInt(minutes)}m`;
    }
    return `${parseInt(minutes)}m`;
  };

  return (
    <Layout>
      <div style={{ padding: '0' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', color: '#F8FAFC', marginBottom: '8px', fontWeight: 600 }}>
            Data Gap Detection
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '15px' }}>
            Identify missing data periods and fill gaps using Python converter tool
          </p>
        </div>

        {/* Gap Detection Section */}
        <div style={{
          backgroundColor: '#1E293B',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <span style={{ fontSize: '18px' }}>🔍</span>
            <h2 style={{ fontSize: '18px', color: '#F8FAFC', fontWeight: 600, margin: 0 }}>
              Query Settings
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '13px', marginBottom: '8px' }}>
                Symbol
              </label>
              <select
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                disabled={loadingSymbols}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#F8FAFC',
                  fontSize: '14px',
                  cursor: loadingSymbols ? 'wait' : 'pointer'
                }}
              >
                {loadingSymbols ? (
                  <option value="">Loading symbols...</option>
                ) : availableSymbols.length === 0 ? (
                  <option value="">No symbols available</option>
                ) : (
                  availableSymbols.map((sym) => (
                    <option key={sym} value={sym}>
                      {sym}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '13px', marginBottom: '8px' }}>
                Timeframe
              </label>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#F8FAFC',
                  fontSize: '14px'
                }}
              >
                <option value="M1">M1 (1 minute)</option>
                <option value="M5">M5 (5 minutes)</option>
                <option value="M15">M15 (15 minutes)</option>
                <option value="M30">M30 (30 minutes)</option>
                <option value="H1">H1 (1 hour)</option>
                <option value="H4">H4 (4 hours)</option>
                <option value="D1">D1 (1 day)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '13px', marginBottom: '8px' }}>
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#F8FAFC',
                  fontSize: '14px'
                }}
              >
                <option value="Last 7 days">Last 7 days</option>
                <option value="Last 30 days">Last 30 days</option>
                <option value="Last 90 days">Last 90 days</option>
                <option value="All time">All time</option>
              </select>
            </div>
          </div>

          <button
            onClick={detectGaps}
            disabled={detecting}
            style={{
              padding: '10px 20px',
              backgroundColor: detecting ? '#475569' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: detecting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🔍</span>
            {detecting ? 'Detecting...' : 'Detect Gaps'}
          </button>

          {errorMessage && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#7F1D1D',
              borderRadius: '6px',
              color: '#FCA5A5',
              fontSize: '13px'
            }}>
              {errorMessage}
            </div>
          )}
        </div>

        {/* Gap Results */}
        {gapData && (
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h2 style={{ fontSize: '18px', color: '#F8FAFC', fontWeight: 600, margin: 0 }}>
                Detected Gaps ({gapData.gaps.length} total, {gapData.missingBars} bars missing)
              </h2>
            </div>

            <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Total Bars</div>
                <div style={{ color: '#F8FAFC', fontSize: '24px', fontWeight: 600 }}>{gapData.totalBars.toLocaleString()}</div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Expected Bars</div>
                <div style={{ color: '#F8FAFC', fontSize: '24px', fontWeight: 600 }}>{gapData.expectedBars.toLocaleString()}</div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Data Completeness</div>
                <div style={{ color: gapData.dataCompleteness >= 95 ? '#10B981' : '#F59E0B', fontSize: '24px', fontWeight: 600 }}>
                  {gapData.dataCompleteness.toFixed(1)}%
                </div>
              </div>
            </div>

            {gapData.gaps.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {gapData.gaps.slice(0, 5).map((gap, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '16px',
                      backgroundColor: '#0F172A',
                      borderRadius: '8px',
                      border: '1px solid #334155'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ color: '#F59E0B', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                          ⚠️ Gap #{gap.gapNumber}
                        </div>
                        <div style={{ color: '#94A3B8', fontSize: '13px', lineHeight: '1.6' }}>
                          <div><strong style={{ color: '#F8FAFC' }}>Start:</strong> {new Date(gap.startTime).toLocaleString()}</div>
                          <div><strong style={{ color: '#F8FAFC' }}>End:</strong> {new Date(gap.endTime).toLocaleString()}</div>
                          <div><strong style={{ color: '#F8FAFC' }}>Duration:</strong> {formatDuration(gap.duration)} ({gap.missingBars} bars)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {gapData.gaps.length > 5 && (
                  <div style={{ color: '#64748B', fontSize: '13px', textAlign: 'center', marginTop: '8px' }}>
                    ... and {gapData.gaps.length - 5} more gaps
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#10B981' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                <div style={{ fontSize: '16px', fontWeight: 500 }}>No gaps detected - data is complete!</div>
              </div>
            )}
          </div>
        )}

        {/* Gap Fill Upload Section */}
        <div style={{
          backgroundColor: '#1E293B',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <span style={{ fontSize: '18px' }}>📁</span>
            <h2 style={{ fontSize: '18px', color: '#F8FAFC', fontWeight: 600, margin: 0 }}>
              Upload Gap Fill JSON
            </h2>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#94A3B8', fontSize: '14px', marginBottom: '12px' }}>
              Use the Python converter tool to generate JSON from MT5/cTrader/Dukascopy CSV files:
            </p>
            <code style={{
              display: 'block',
              padding: '12px',
              backgroundColor: '#0F172A',
              borderRadius: '6px',
              color: '#3B82F6',
              fontSize: '13px',
              fontFamily: 'monospace',
              overflowX: 'auto'
            }}>
              python convert_ohlc.py XAUUSD_M1.csv --symbol XAUUSD --timeframe M1
            </code>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: dragOver ? '2px dashed #3B82F6' : '2px dashed #334155',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: dragOver ? '#1E3A5F' : '#0F172A',
              cursor: 'pointer',
              marginBottom: '20px',
              transition: 'all 0.2s'
            }}
            onClick={() => document.getElementById('json-file-input')?.click()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📄</div>
            <div style={{ color: '#F8FAFC', fontSize: '15px', marginBottom: '8px' }}>
              {jsonFile ? jsonFile.name : 'Drag & drop or click to upload'}
            </div>
            <div style={{ color: '#64748B', fontSize: '13px' }}>
              Supported: JSON files from Python converter (max 10MB)
            </div>
            <input
              id="json-file-input"
              type="file"
              accept=".json"
              onChange={handleJsonFileChange}
              style={{ display: 'none' }}
            />
          </div>

          <button
            onClick={uploadPreview}
            disabled={!jsonFile || uploading}
            style={{
              padding: '10px 20px',
              backgroundColor: !jsonFile || uploading ? '#475569' : '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !jsonFile || uploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📤</span>
            {uploading ? 'Uploading...' : 'Upload & Preview'}
          </button>
        </div>

        {/* Preview Section */}
        {preview && (
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: preview.readyToImport ? '1px solid #10B981' : '1px solid #F59E0B'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ fontSize: '18px' }}>{preview.readyToImport ? '✅' : '⚠️'}</span>
              <h2 style={{ fontSize: '18px', color: '#F8FAFC', fontWeight: 600, margin: 0 }}>
                Preview: {preview.metadata.symbol} {preview.metadata.timeframe}
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Total Bars</div>
                <div style={{ color: '#F8FAFC', fontSize: '20px', fontWeight: 600 }}>{preview.totalBars.toLocaleString()}</div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Source Platform</div>
                <div style={{ color: '#F8FAFC', fontSize: '20px', fontWeight: 600 }}>{preview.metadata.sourcePlatform}</div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#0F172A', borderRadius: '8px' }}>
                <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '4px' }}>Gaps to Fill</div>
                <div style={{ color: '#10B981', fontSize: '20px', fontWeight: 600 }}>{preview.gapsFilled}</div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '8px' }}>Validation Status:</div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '6px 12px',
                  backgroundColor: preview.validation.validBars > 0 ? '#065F46' : '#7F1D1D',
                  color: preview.validation.validBars > 0 ? '#10B981' : '#FCA5A5',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  ✓ {preview.validation.validBars} valid bars
                </span>
                {preview.validation.invalidBars > 0 && (
                  <span style={{
                    padding: '6px 12px',
                    backgroundColor: '#7F1D1D',
                    color: '#FCA5A5',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    ✗ {preview.validation.invalidBars} invalid bars
                  </span>
                )}
                {preview.validation.warnings.length > 0 && (
                  <span style={{
                    padding: '6px 12px',
                    backgroundColor: '#78350F',
                    color: '#FCD34D',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    ⚠ {preview.validation.warnings.length} warnings
                  </span>
                )}
              </div>
            </div>

            {preview.validation.errors.length > 0 && (
              <div style={{
                padding: '12px',
                backgroundColor: '#7F1D1D',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <div style={{ color: '#FCA5A5', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                  Validation Errors:
                </div>
                {preview.validation.errors.map((error, i) => (
                  <div key={i} style={{ color: '#FCA5A5', fontSize: '12px', marginLeft: '12px' }}>
                    • {error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={executeImport}
                disabled={!preview.readyToImport || importing}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !preview.readyToImport || importing ? '#475569' : '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: !preview.readyToImport || importing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>⚡</span>
                {importing ? 'Importing...' : `Fill Gap - Import ${preview.totalBars} Bars`}
              </button>
              <button
                onClick={() => setPreview(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#334155',
                  color: '#F8FAFC',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Import Jobs Section */}
        {importJobs.length > 0 && (
          <div style={{
            backgroundColor: '#1E293B',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ fontSize: '18px' }}>📋</span>
              <h2 style={{ fontSize: '18px', color: '#F8FAFC', fontWeight: 600, margin: 0 }}>
                Import Jobs ({importJobs.length})
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {importJobs.slice(0, 5).map((job) => (
                <div
                  key={job.jobId}
                  style={{
                    padding: '16px',
                    backgroundColor: '#0F172A',
                    borderRadius: '8px',
                    border: '1px solid #334155'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#F8FAFC', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                        {job.jobId}
                      </div>
                      <div style={{ color: '#64748B', fontSize: '12px' }}>
                        {new Date(job.startedAt).toLocaleString()}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: '4px 12px',
                        backgroundColor:
                          job.status === 'Completed' ? '#065F46' :
                          job.status === 'Failed' ? '#7F1D1D' :
                          '#78350F',
                        color:
                          job.status === 'Completed' ? '#10B981' :
                          job.status === 'Failed' ? '#FCA5A5' :
                          '#FCD34D',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                    >
                      {job.status}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', fontSize: '13px' }}>
                    <div>
                      <div style={{ color: '#94A3B8' }}>Inserted</div>
                      <div style={{ color: '#F8FAFC', fontWeight: 600 }}>{job.barsInserted.toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ color: '#94A3B8' }}>Skipped</div>
                      <div style={{ color: '#F8FAFC', fontWeight: 600 }}>{job.barsSkipped.toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ color: '#94A3B8' }}>Updated</div>
                      <div style={{ color: '#F8FAFC', fontWeight: 600 }}>{job.barsUpdated.toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ color: '#94A3B8' }}>Gaps Filled</div>
                      <div style={{ color: '#10B981', fontWeight: 600 }}>{job.gapsFilled.toLocaleString()}</div>
                    </div>
                  </div>

                  {job.errorMessage && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px',
                      backgroundColor: '#7F1D1D',
                      borderRadius: '4px',
                      color: '#FCA5A5',
                      fontSize: '12px'
                    }}>
                      Error: {job.errorMessage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Gaps;
